import { AnimatePresence } from "motion/react";

import { useTheme } from "@up/data";
import { Toggle } from "@up/ui";

import { useCart } from "./CartContext";
import StudentInformationDisplay from "./StudentInformationDisplay";
import StudentInformationForm from "./StudentInformationForm";

import type { TCart } from "@up/data";

type Props = {
  cart: TCart;
  dobRequired: boolean;
};

export const studentDataIsValid = function (
  cart?: TCart,
  dobRequired?: boolean,
): boolean {
  const student = cart?.student;
  if (!student?.firstName || !student?.lastName) {
    return false;
  }
  if (!student?.address?.country) {
    return false;
  }
  if (student.address.country === "United States" && !student.address.zipCode) {
    return false;
  }
  if (dobRequired && !student.dob) {
    return false;
  }

  const errors = cart?.errors ?? [];
  const studentErrors = errors
    .filter((e) => {
      return typeof e.path[0] === "string";
    })
    .map((e) => {
      const path = e.path[0] as string;
      return path.match(/^student/);
    })
    .filter((m) => Boolean(m));

  return !studentErrors.length;
};

export const StudentSection = function StudentSection({
  cart,
  dobRequired,
}: Props) {
  const theme = useTheme();
  const { updateCart } = useCart();
  return (
    <Toggle>
      {(showForm, setShowForm) => {
        return (
          <section
            id="student-section"
            className="checkout-section scroll-mt-16 mb-16"
          >
            <div className="mb-2 flex items-center">
              <h2 className={`checkout-heading ${theme.heading2Class}`}>
                Student Information
              </h2>
              <button
                className="ml-3 cursor-pointer rounded border border-solid border-gray-400 bg-transparent px-3 py-1 text-sm text-inherit"
                onClick={() => {
                  setShowForm(!showForm);
                }}
              >
                Edit
              </button>
            </div>

            <AnimatePresence>
              {showForm || !studentDataIsValid(cart, dobRequired) ? (
                <StudentInformationForm
                  key="StudentInformationForm"
                  close={() => {
                    setShowForm(false);
                  }}
                  saveSuccess={(updatedCart) => {
                    updateCart(updatedCart);
                    setShowForm(false);
                  }}
                  student={cart.student}
                  dobRequired={dobRequired}
                />
              ) : (
                <StudentInformationDisplay student={cart.student} />
              )}
            </AnimatePresence>
          </section>
        );
      }}
    </Toggle>
  );
};
